import { useEffect, useState, useCallback } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MainLayout } from '@/components/layouts/MainLayout'
import { LandingPage } from '@/components/views/LandingPage'
import { AuthPage } from '@/components/views/AuthPage'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/services/api-client'
import { useThemeStore } from '@/stores/theme'
import '@styles/globals.css'
import '@styles/layout.css'
import '@styles/components/header.css'
import '@styles/components/sidebar.css'
import '@styles/components/right-panel.css'
import '@styles/components/wysiwyg.css'
import '@styles/views/dashboard.css'
import '@styles/views/project-explorer.css'
import '@styles/views/thread-view.css'
import '@styles/views/settings.css'
import '@styles/views/landing.css'
import '@styles/views/auth.css'
import '@styles/views/wallet.css'
import '@styles/views/marketplace.css'
import '@styles/views/admin.css'
import '@styles/views/api-keys.css'
import '@styles/views/app-builder.css'
import '@styles/views/agent.css'
import '@styles/views/social-agent.css'
import '@styles/views/content-seo.css'
import '@styles/views/file-manager.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

type PublicView = 'landing' | 'login' | 'register'

function App() {
  const { isAuthenticated, isLoading, hydrate, logout } = useAuthStore()
  const [publicView, setPublicView] = useState<PublicView>('landing')
  const { theme } = useThemeStore()

  useEffect(() => {
    hydrate()

    // Validate session on load
    const token = localStorage.getItem('synapsios_token')
    if (token) {
      api.getMe().catch(() => {
        logout()
      })
    }
  }, [hydrate, logout])

  // Listen for forced logout (401 from API)
  useEffect(() => {
    const handleLogout = () => {
      logout()
      queryClient.clear()
      setPublicView('login')
    }
    window.addEventListener('synapsios:logout', handleLogout)
    return () => window.removeEventListener('synapsios:logout', handleLogout)
  }, [logout])

  // Landing page & auth always use dark theme, user theme applied only when logged in
  useEffect(() => {
    if (!isAuthenticated) {
      document.documentElement.setAttribute('data-theme', 'dark')
    } else {
      document.documentElement.setAttribute('data-theme', theme)
    }
  }, [isAuthenticated, theme])

  const handleNavigate = useCallback((view: PublicView | 'landing') => {
    setPublicView(view as PublicView)
  }, [])

  if (isLoading) {
    return (
      <div style={{ width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0e14' }}>
        <div className="spinner" style={{ width: 32, height: 32, border: '3px solid #2a3545', borderTopColor: '#4a9eff', borderRadius: '50%' }} />
      </div>
    )
  }

  if (!isAuthenticated) {
    if (publicView === 'login') return <AuthPage mode="login" onNavigate={handleNavigate} />
    if (publicView === 'register') return <AuthPage mode="register" onNavigate={handleNavigate} />
    return <LandingPage onNavigate={handleNavigate} />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <MainLayout />
    </QueryClientProvider>
  )
}

export default App
